import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

// Draggable before/after comparison slider. After-image sits on the left of the handle.
export const BeforeAfter = ({ before, after, altBefore = "Before", altAfter = "After", className = "" }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      role="slider"
      aria-label="Before and after comparison"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => e.buttons === 1 && update(e.clientX)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
      }}
      className={`relative w-full aspect-[4/3] select-none overflow-hidden cursor-ew-resize touch-pan-y ${className}`}
    >
      {/* Bottom layer: BEFORE */}
      <img src={before} alt={altBefore} data-testid="ba-before" draggable={false} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      {/* Top layer: AFTER, clipped to the left of the handle */}
      <img
        src={after}
        alt={altAfter}
        data-testid="ba-after"
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* Divider + handle */}
      <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-weld shadow-[0_0_14px_rgba(57,255,20,0.8)]" />
        <div data-testid="ba-handle" className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-weld text-ink flex items-center justify-center shadow-glow border-2 border-ink/40">
          <MoveHorizontal size={18} />
        </div>
      </div>

      {/* Labels */}
      <span data-testid="ba-label-after" className="absolute top-3 left-3 bg-ink/70 backdrop-blur-sm border border-weld/40 text-weld-glow text-xs font-semibold tracking-widest uppercase px-2.5 py-1 rounded-md">After</span>
      <span data-testid="ba-label-before" className="absolute top-3 right-3 bg-ink/70 backdrop-blur-sm border border-white/20 text-white/80 text-xs font-semibold tracking-widest uppercase px-2.5 py-1 rounded-md">Before</span>
    </div>
  );
};
